// services/chatConversationParsing.ts
//
// The conversation shapes and their defensive parsers for the companion's
// conversation history (ORA-458). The routes are in `chatConversations.ts`,
// which re-exports the public names below; import them from there.

import type { PersistedToolBlock } from '@/types/legacyChat';
import type {
  ChatBlockPlacement,
  ChatCitation,
  ChatContextLabel,
  ChatFollowup,
  ChatMessageActivity,
  ChatNavigationTarget,
} from '../types/chatStream';
import { parseFactIds, parseRegions } from '../utils/documentRegions';
import type { ChatActionProposal } from '../slices/chat/chatSlice';
import { readChatActionResult, readChatActionUndo } from './chatActionPayload';

/** One row in the history list. */
export interface ChatConversationSummary {
  id: string;
  title: string | null;
  /** ISO-8601, or null when the server omitted it. */
  lastMessageAt: string | null;
  /** ISO-8601 when the student pinned this conversation. */
  pinnedAt: string | null;
  /** Written by the old pipeline: it opens read-only. */
  legacy: boolean;
}

export interface ChatConversationPage {
  conversations: ChatConversationSummary[];
  hasMore: boolean;
}

/** One restored block, already in the shape `conversationHydrated` takes. */
export interface ChatConversationBlock {
  id: string;
  kind: string;
  placement: ChatBlockPlacement;
  payload: Record<string, unknown>;
  target: ChatNavigationTarget | null;
}

/**
 * An image the student attached, as history returns it.
 *
 * The URL renders a restored image. Newer reads also include the storage
 * metadata needed to retry it; older backend deploys omit those optional
 * fields and remain renderable.
 */
export interface ChatRestoredAttachment {
  id: string;
  kind?: 'image' | 'document';
  documentId?: string;
  fileName?: string;
  pageCount?: number | null;
  /** Presigned, one hour. Documents open through their owner-signed route. */
  url?: string;
  mimeType: string;
  /** Present once the backend returning retry metadata is deployed. */
  key?: string;
  sizeBytes?: number;
}

export interface ChatConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  attachments: ChatRestoredAttachment[];
  citations: ChatCitation[];
  blocks: ChatConversationBlock[];
  /**
   * The follow-up questions this answer offered (ORA-606). Empty for a user
   * message, for an answer the model suggested none for, and for a backend
   * that does not send them yet — there is no client-side substitute.
   */
  followups: ChatFollowup[];
  /**
   * What this answer did while it was working (ORA-604), or null when the
   * turn recorded nothing (and on every message an older backend returns).
   */
  activity: ChatMessageActivity | null;
  /** Vetted turn context, returned only on the associated user message. */
  contextLabel?: ChatContextLabel;
  /** Executed action receipt restored onto its proposing assistant message. */
  actionProposal?: ChatActionProposal;
}

export interface ChatConversationDetail {
  id: string;
  title: string | null;
  pinnedAt: string | null;
  legacy: boolean;
  messages: ChatConversationMessage[];
  /**
   * The old panel's persisted tool blocks, as stored. Present on legacy
   * conversations only; the caller maps them onto the block contract.
   */
  legacyToolBlocks: PersistedToolBlock[];
}

export const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const asNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

export const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

export const parseSummary = (
  value: unknown
): ChatConversationSummary | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  if (!raw || !id) return null;

  return {
    id,
    title: asNonEmptyString(raw.title),
    lastMessageAt: asNonEmptyString(raw.lastMessageAt),
    pinnedAt: asNonEmptyString(raw.pinnedAt),
    legacy: raw.legacy === true,
  };
};

const parseAttachment = (value: unknown): ChatRestoredAttachment | null => {
  const raw = record(value);
  const isDocument = raw?.kind === 'document';
  const documentId = asNonEmptyString(raw?.documentId);
  const id = asNonEmptyString(raw?.id) ?? documentId;
  const url = asNonEmptyString(raw?.url);
  // Images need a presigned rendering URL. A document deliberately does not:
  // it opens later through an owner-checked signed-source endpoint.
  if (!raw || !id || (!isDocument && !url) || (isDocument && !documentId)) {
    return null;
  }

  return {
    id,
    ...(url ? { url } : {}),
    mimeType: asNonEmptyString(raw.mimeType) ?? 'application/octet-stream',
    ...(isDocument ? { kind: 'document' as const } : {}),
    ...(documentId ? { documentId } : {}),
    ...(asNonEmptyString(raw.fileName)
      ? { fileName: asNonEmptyString(raw.fileName) as string }
      : {}),
    ...(typeof raw.pageCount === 'number' ? { pageCount: raw.pageCount } : {}),
    ...(asNonEmptyString(raw.key)
      ? { key: asNonEmptyString(raw.key) as string }
      : {}),
    ...(typeof raw.sizeBytes === 'number' && Number.isFinite(raw.sizeBytes)
      ? { sizeBytes: raw.sizeBytes }
      : {}),
  };
};

const parseCitation = (value: unknown): ChatCitation | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  const kind = asNonEmptyString(raw?.kind);
  // An own-data citation (ORA-514) names no content row: its `contentId` is
  // the empty string, which is well-formed, unlike a missing one.
  const contentId = typeof raw?.contentId === 'string' ? raw.contentId : null;
  if (!raw || !id || !kind || contentId === null) return null;

  const target = parseTarget(raw.target);
  // Document-model highlights (Document Pipeline v4) ride only on lecture
  // page citations; an older citation keeps its old shape.
  const factIds = kind === 'document_page' ? parseFactIds(raw.factIds) : [];
  const regions = kind === 'document_page' ? parseRegions(raw.regions) : [];

  return {
    id,
    kind,
    contentId,
    ...(asNonEmptyString(raw.title)
      ? { title: asNonEmptyString(raw.title) as string }
      : {}),
    sectionId: asNonEmptyString(raw.sectionId),
    page: typeof raw.page === 'number' ? raw.page : null,
    ...(asNonEmptyString(raw.lectureId)
      ? { lectureId: asNonEmptyString(raw.lectureId) as string }
      : {}),
    ...(asNonEmptyString(raw.documentId)
      ? { documentId: asNonEmptyString(raw.documentId) as string }
      : {}),
    ...(asNonEmptyString(raw.excerpt)
      ? { excerpt: asNonEmptyString(raw.excerpt) as string }
      : {}),
    ...(asNonEmptyString(raw.label)
      ? { label: asNonEmptyString(raw.label) as string }
      : {}),
    ...(typeof raw.rendered === 'boolean' ? { rendered: raw.rendered } : {}),
    ...(typeof raw.addable === 'boolean' ? { addable: raw.addable } : {}),
    ...(target ? { target } : {}),
    ...(factIds.length > 0 ? { factIds } : {}),
    ...(regions.length > 0 ? { regions } : {}),
  };
};

/**
 * One follow-up chip (ORA-606). Only `kind: 'prompt'` survives the read: the
 * navigate chip is part of the protocol type but nothing has produced one for
 * a long time, and the server drops them too — a chip the client cannot send
 * is not a chip.
 */
const parseFollowup = (value: unknown): ChatFollowup | null => {
  const raw = record(value);
  if (!raw || raw.kind !== 'prompt') return null;

  const id = asNonEmptyString(raw.id);
  const label = asNonEmptyString(raw.label);
  const prompt = asNonEmptyString(raw.prompt);
  if (!id || !label || !prompt) return null;

  return {
    id,
    kind: 'prompt',
    label,
    prompt,
    // Kept across a reload, so a study chip is still drawn as one (ORA-827).
    ...(raw.intent === 'study' ? { intent: 'study' as const } : {}),
  };
};

/**
 * The step trail a persisted turn showed (ORA-604). A record whose version
 * this client does not know, or one with no steps in it, is nothing to draw.
 */
const parseActivity = (value: unknown): ChatMessageActivity | null => {
  const raw = record(value);
  if (!raw || raw.version !== 1) return null;

  const statusTrail = asArray(raw.statusTrail).flatMap((label) =>
    asNonEmptyString(label) ? [asNonEmptyString(label) as string] : []
  );

  if (statusTrail.length === 0) return null;

  return {
    version: 1,
    statusTrail,
    ms:
      typeof raw.ms === 'number' && Number.isFinite(raw.ms) && raw.ms > 0
        ? raw.ms
        : 0,
  };
};

const CONTEXT_KINDS = new Set<ChatContextLabel['kind']>([
  'question',
  'flashcard',
  'lecture',
  'video',
  'article',
  'exam',
]);

const parseContextLabel = (value: unknown): ChatContextLabel | undefined => {
  const raw = record(value);
  const kind = asNonEmptyString(raw?.kind);
  const label = asNonEmptyString(raw?.label)?.trim();

  if (!kind || !CONTEXT_KINDS.has(kind as ChatContextLabel['kind']))
    return undefined;
  if (!label || label.length > 100) return undefined;

  const lectureId = asNonEmptyString(raw?.lectureId);
  return {
    kind: kind as ChatContextLabel['kind'],
    label,
    ...(kind === 'lecture' && lectureId ? { lectureId } : {}),
  };
};

const parseTarget = (value: unknown): ChatNavigationTarget | null => {
  const raw = record(value);
  const route = asNonEmptyString(raw?.route);
  if (!raw || !route) return null;

  const params = record(raw.params);

  return {
    route,
    ...(params
      ? {
          params: Object.fromEntries(
            Object.entries(params).flatMap(([key, entry]) =>
              typeof entry === 'string' ? [[key, entry] as const] : []
            )
          ),
        }
      : {}),
  };
};

const parseBlock = (value: unknown): ChatConversationBlock | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  const kind = asNonEmptyString(raw?.kind);
  const payload = record(raw?.payload);
  if (!raw || !id || !kind || !payload) return null;

  return {
    id,
    kind,
    placement: raw.placement === 'inline' ? 'inline' : 'end',
    payload,
    target: parseTarget(raw.target),
  };
};

const parseActionProposal = (value: unknown): ChatActionProposal | null => {
  const raw = record(value);
  const actionId = asNonEmptyString(raw?.actionId);
  const name = asNonEmptyString(raw?.name);
  const outcome = record(raw?.outcome);
  if (!raw || !actionId || !name || !outcome) return null;

  const result = readChatActionResult(outcome.result);

  const resultUndo =
    result?.kind === 'plan_change' || result?.kind === 'study_change'
      ? (result.undo ?? null)
      : null;

  const restoredUndo = readChatActionUndo(raw.undo);

  // History's top-level copy is authoritative for current availability, while
  // the sanitized result carries optional presentation fields such as the
  // approximate-restore note. Keep both halves on reload.
  const undo = restoredUndo
    ? { ...(resultUndo ?? {}), ...restoredUndo }
    : resultUndo;

  const details = asArray(raw.details).flatMap((entry) => {
    const detail = record(entry);
    const label = asNonEmptyString(detail?.label);
    const detailValue = asNonEmptyString(detail?.value);
    return label && detailValue ? [{ label, value: detailValue }] : [];
  });

  return {
    actionId,
    // Replaced with the hydrated message id before the slice stores it.
    turnId: asNonEmptyString(raw.turnId) ?? '',
    name,
    args: record(raw.args) ?? {},
    summary: asNonEmptyString(raw.summary) ?? '',
    ...(details.length > 0 ? { details } : {}),
    ...(asNonEmptyString(raw.confirmLabel)
      ? { confirmLabel: asNonEmptyString(raw.confirmLabel) as string }
      : {}),
    status: undo?.undoneAt ? 'undone' : 'confirmed',
    outcome: {
      summary: asNonEmptyString(outcome.summary),
      target: parseTarget(outcome.target),
      result,
    },
    undo,
  };
};

export const parseMessage = (
  value: unknown
): ChatConversationMessage | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  if (!raw || !id) return null;

  // Anything that is not explicitly the assistant is the student's own turn;
  // a role we do not recognise must never be drawn as if Ora had said it.
  const role = raw.role === 'assistant' ? 'assistant' : 'user';

  const contextLabel =
    role === 'user' ? parseContextLabel(raw.contextLabel) : undefined;

  const actionProposal =
    role === 'assistant' ? parseActionProposal(raw.actionProposal) : null;

  return {
    id,
    role,
    text: typeof raw.text === 'string' ? raw.text : '',
    attachments: asArray(raw.attachments)
      .map(parseAttachment)
      .filter((item): item is ChatRestoredAttachment => item !== null),
    citations: asArray(raw.citations)
      .map(parseCitation)
      .filter((item): item is ChatCitation => item !== null),
    blocks: asArray(raw.blocks)
      .map(parseBlock)
      .filter((item): item is ChatConversationBlock => item !== null),
    followups: asArray(raw.followups)
      .map(parseFollowup)
      .filter((item): item is ChatFollowup => item !== null),
    activity: parseActivity(raw.activity),
    ...(contextLabel ? { contextLabel } : {}),
    ...(actionProposal ? { actionProposal } : {}),
  };
};

/**
 * A legacy tool block as the old panel persisted it. Only the fields the
 * mapper reads are required; anything else rides along untouched.
 */
export const parseLegacyToolBlock = (
  value: unknown
): PersistedToolBlock | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  const messageId = asNonEmptyString(raw?.messageId);
  const kind = asNonEmptyString(raw?.kind);
  if (!raw || !id || !messageId || !kind) return null;

  return { ...raw, id, messageId, kind } as PersistedToolBlock;
};

/**
 * One conversation as the wire carries it, parsed defensively. Shared with the
 * admin review read (ORA-700), which returns the same conversation shape.
 */
export const parseChatConversationDetail = (
  value: unknown
): ChatConversationDetail | null => {
  const raw = record(value);
  const id = asNonEmptyString(raw?.id);
  if (!raw || !id) return null;

  return {
    id,
    title: asNonEmptyString(raw.title),
    pinnedAt: asNonEmptyString(raw.pinnedAt),
    legacy: raw.legacy === true,
    messages: asArray(raw.messages)
      .map(parseMessage)
      .filter((item): item is ChatConversationMessage => item !== null),
    legacyToolBlocks: asArray(raw.legacyToolBlocks)
      .map(parseLegacyToolBlock)
      .filter((item): item is PersistedToolBlock => item !== null),
  };
};
